import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import { toast } from 'sonner';
import GlassCard from '../components/GlassCard';
import HeroFan from '../components/HeroFan';
import AppCard from '../components/AppCard';
import Reveal from '../components/Reveal';
import { LineIcon, BrandIcon } from '../components/icons';
import { usePressSpring } from '../hooks/usePressSpring';
import { APPS } from '../data/apps';
import { STUDIO } from '../data/studio';
import { useLanguage } from '../i18n/LanguageContext';
import { cx } from '../utils/cx';
import mixins from '../styles/mixins.module.css';
import styles from './Home.module.css';

const SECTION_FOR_PATH = { '/': null, '/apps': 'apps', '/studio': 'studio', '/contact': 'contact' };

function scrollToSection(id, instant) {
  if (!id) {
    window.scrollTo({ top: 0, behavior: instant ? 'instant' : 'smooth' });
    return;
  }
  const el = document.getElementById(id);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - 60;
  window.scrollTo({ top, behavior: instant ? 'instant' : 'smooth' });
}

function PrincipleCard({ icon, title, children }) {
  return (
    <GlassCard as="div" tilt press className={styles.pr}>
      <div className={styles.pi}>
        <LineIcon name={icon} />
      </div>
      <h3>{title}</h3>
      <p>{children}</p>
    </GlassCard>
  );
}

function SocialRow({ className }) {
  return (
    <div className={cx(mixins.social, className)}>
      <a href={STUDIO.social.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" title="LinkedIn">
        <BrandIcon name="linkedin" />
      </a>
      <a href={STUDIO.social.medium} target="_blank" rel="noopener noreferrer" aria-label="Medium" title="Medium">
        <BrandIcon name="medium" />
      </a>
      <a href={STUDIO.social.github} target="_blank" rel="noopener noreferrer" aria-label="GitHub" title="GitHub">
        <BrandIcon name="github" />
      </a>
      <a href={STUDIO.social.x} target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)" title="X (Twitter)">
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      </a>
    </div>
  );
}

function ContactCard() {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const sendRef = usePressSpring(mixins.pressed);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !message.trim()) {
      toast.error(t('contactFillAllFields'));
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error(t('contactInvalidEmail'));
      return;
    }

    setIsSending(true);
    const loadingToast = toast.loading(t('contactSendingToast'));
    try {
      await emailjs.send(
        'service_5kwrjcn',
        'template_dhbnnwi',
        { from_email: email, message, to_email: STUDIO.email },
        'dTVDs-r1ZdrjA0ILN'
      );
      toast.success(t('contactSentToast'), { id: loadingToast });
      setEmail('');
      setMessage('');
    } catch (error) {
      console.error('EmailJS Error:', error);
      toast.error(t('contactFailedToast'), { id: loadingToast });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <GlassCard as="div" className={styles.contact}>
      <div className={cx(mixins.halo, styles.contactHalo)} />
      <h2 className={cx(mixins.hLg, mixins.ink)}>
        {t('contactTitleLine1')}
        <br />
        <span className={mixins.soft}>{t('contactTitleLine2')}</span>
      </h2>
      <p className={cx(mixins.lead, styles.contactLead)}>{t('contactLead')}</p>
      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.formGroup}>
          <label htmlFor="contact-email">{t('contactEmailLabel')}</label>
          <input
            id="contact-email"
            type="email"
            placeholder={t('contactEmailPlaceholder')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isSending}
          />
        </div>
        <div className={styles.formGroup}>
          <label htmlFor="contact-message">{t('contactMessageLabel')}</label>
          <textarea
            id="contact-message"
            rows={4}
            placeholder={t('contactMessagePlaceholder')}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={isSending}
          />
        </div>
        <button ref={sendRef} type="submit" className={cx(mixins.btn, mixins.btnSolid, mixins.spring)} disabled={isSending}>
          {isSending ? t('contactSending') : t('contactSend')}
        </button>
      </form>
      <SocialRow className={styles.contactSocial} />
    </GlassCard>
  );
}

export default function Home() {
  const { t } = useLanguage();
  const { pathname } = useLocation();
  const hasMounted = useRef(false);
  const heroActionRef = usePressSpring(mixins.pressed);
  const ghostActionRef = usePressSpring(mixins.pressed);

  useEffect(() => {
    const section = SECTION_FOR_PATH[pathname] ?? null;
    const instant = !hasMounted.current;
    hasMounted.current = true;
    if (instant) {
      requestAnimationFrame(() => scrollToSection(section, true));
    } else {
      scrollToSection(section, false);
    }
  }, [pathname]);

  return (
    <>
      <section id="home" className={styles.hero}>
        <div className={cx(mixins.wrap, styles.heroGrid)}>
          <Reveal>
            <h1 className={cx(mixins.hXl, mixins.ink, styles.heroTitle)}>{t('heroTitle')}</h1>
            <p className={cx(mixins.lead, styles.heroLead)}>{t('heroLead')}</p>
            <div className={styles.actions}>
              <Link ref={heroActionRef} className={cx(mixins.btn, mixins.btnSolid, mixins.spring)} to="/apps">
                {t('heroOurApps')} <LineIcon name="arrow" strokeWidth={2.3} />
              </Link>
              <Link ref={ghostActionRef} className={cx(mixins.btn, mixins.btnGhost, mixins.spring)} to="/contact">
                {t('heroGetInTouch')}
              </Link>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <HeroFan />
          </Reveal>
        </div>
      </section>

      <section id="apps" className={cx(mixins.section, styles.cv)}>
        <div className={mixins.wrap}>
          <Reveal className={mixins.sectionHead}>
            <h2 className={cx(mixins.hLg, mixins.ink)}>{t('appsTitle')}</h2>
          </Reveal>
          <div className={styles.apps}>
            {APPS.map((app, i) => (
              <AppCard key={app.id} app={app} delay={i * 90} />
            ))}
          </div>
        </div>
      </section>

      <section id="studio" className={cx(mixins.section, styles.cv)}>
        <div className={mixins.wrap}>
          <Reveal className={mixins.sectionHead}>
            <h2 className={cx(mixins.hLg, mixins.ink)}>{t('studioTitle')}</h2>
          </Reveal>
          <Reveal>
            <GlassCard as="div" className={styles.statement}>
              <p className={styles.big}>
                {t('studioStatementBig')}{' '}
                <span className={mixins.soft}>{t('studioStatementSoft')}</span>
              </p>
              <div className={styles.side}>
                <p>{t('studioSideP')}</p>
                <div className={styles.founder}>
                  <div>
                    <b>{STUDIO.founder}</b>
                    <small>{t('studioFounderTitle')}</small>
                  </div>
                  <SocialRow />
                </div>
              </div>
            </GlassCard>
          </Reveal>
          <div className={styles.principles}>
            <Reveal delay={0}>
              <PrincipleCard icon="shield" title={t('principleSafetyTitle')}>
                {t('principleSafetyBody')}
              </PrincipleCard>
            </Reveal>
            <Reveal delay={90}>
              <PrincipleCard icon="build" title={t('principleProductTitle')}>
                {t('principleProductBody')}
              </PrincipleCard>
            </Reveal>
            <Reveal delay={180}>
              <PrincipleCard icon="code" title={t('principleMaintainableTitle')}>
                {t('principleMaintainableBody')}
              </PrincipleCard>
            </Reveal>
          </div>
        </div>
      </section>

      <section id="contact" className={cx(mixins.section, styles.cv)}>
        <Reveal className={mixins.wrap}>
          <ContactCard />
        </Reveal>
      </section>
    </>
  );
}
